/**
 * Image Harvester Service
 * Automatically scrapes, validates, and uploads images from websites
 */

import { openaiService } from './openaiService';
import { assetStorageService } from './assetStorageService';

export interface ScrapedImage {
  url: string;
  alt: string;
  dimensions: { width: number; height: number };
  aspectRatio: string; // '1:1', '16:9', '4:3', '1.91:1'
  fileSize?: number;
  format: 'jpg' | 'png' | 'webp' | 'unknown';
  context: string; // Where on page (hero, product, gallery, logo)
  suitable: boolean; // Google Ads compliant
  quality: number; // 0-100
}

export interface StoredAsset {
  url: string;
  originalUrl: string;
  aspectRatio: string;
  dimensions: { width: number; height: number };
  alt: string;
  source: 'website' | 'upload' | 'generated';
  uploadedAt: Date;
  category?: 'logo' | 'square' | 'landscape' | 'portrait';
}

export const imageHarvester = {
  /**
   * Scrape all suitable images from a website
   */
  async scrapeImages(url: string): Promise<ScrapedImage[]> {
    console.log('📸 Harvesting images from:', url);

    try {
      // Use OpenAI to analyze and extract images
      const prompt = `Analyze this website and find ALL images suitable for Google Ads:

URL: ${url}

For each image, extract:
1. Image URL (full URL, not relative)
2. Alt text
3. Context (hero image, product photo, gallery, logo, etc.)
4. Estimated quality (0-100)

RULES for Google Ads suitability:
- Minimum 300x300 pixels
- Professional quality
- Relevant to business
- No excessive text overlay (max 20%)
- Not decorative icons or UI elements
- Focus on: Products, Services, People, Locations, Logos

Return JSON:
{
  "images": [
    {
      "url": "https://...",
      "alt": "Product photo",
      "context": "product",
      "quality": 85,
      "suitable": true,
      "estimatedDimensions": {"width": 1200, "height": 800}
    }
  ]
}`;

      const response = await openaiService.generalQuery(
        prompt,
        'You are an image analysis specialist. Return only valid JSON.'
      );

      const data = JSON.parse(response.replace(/```json\n?/g, '').replace(/```/g, '').trim());
      const images = data.images || [];

      console.log(`✅ Found ${images.length} potential images`);

      // Calculate aspect ratios and formats
      const processed: ScrapedImage[] = images.map((img: any) => {
        const dims = img.estimatedDimensions || { width: 0, height: 0 };
        const aspectRatio = this.calculateAspectRatio(dims.width, dims.height);
        const format = this.detectFormat(img.url);

        return {
          url: img.url,
          alt: img.alt || '',
          dimensions: dims,
          aspectRatio,
          format,
          context: img.context || 'unknown',
          suitable: img.suitable !== false && dims.width >= 300,
          quality: img.quality || 70
        };
      });

      // Filter suitable images
      const suitable = processed.filter(img => 
        img.suitable && 
        img.quality >= 70 &&
        img.dimensions.width >= 300
      );

      console.log(`✅ ${suitable.length} images are Google Ads compliant`);
      return suitable;

    } catch (error) {
      console.error('Error scraping images:', error);
      return [];
    }
  },

  /**
   * Calculate aspect ratio string
   */
  calculateAspectRatio(width: number, height: number): string {
    if (width === 0 || height === 0) return 'unknown';

    const ratio = width / height;

    // Common aspect ratios for Google Ads
    if (Math.abs(ratio - 1) < 0.1) return '1:1'; // Square
    if (Math.abs(ratio - 1.91) < 0.1) return '1.91:1'; // Landscape
    if (Math.abs(ratio - 1.78) < 0.1) return '16:9'; // Video
    if (Math.abs(ratio - 1.33) < 0.1) return '4:3'; // Standard
    if (Math.abs(ratio - 0.8) < 0.1) return '4:5'; // Portrait

    return `${width}:${height}`;
  },

  /**
   * Detect image format from URL
   */
  detectFormat(url: string): ScrapedImage['format'] {
    const lower = url.toLowerCase();
    if (lower.includes('.jpg') || lower.includes('.jpeg')) return 'jpg';
    if (lower.includes('.png')) return 'png';
    if (lower.includes('.webp')) return 'webp';
    return 'unknown';
  },

  /**
   * Harvest and store images in Firebase
   */
  async harvestAndStoreImages(
    url: string,
    orgId: string,
    onProgress?: (message: string) => void
  ): Promise<StoredAsset[]> {
    onProgress?.('🔍 Suche Bilder auf der Website...');
    
    const images = await this.scrapeImages(url);
    
    if (images.length === 0) {
      onProgress?.('⚠️ Keine geeigneten Bilder gefunden');
      return [];
    }

    onProgress?.(`📸 ${images.length} Bilder gefunden, lade hoch...`);
    
    const stored: StoredAsset[] = [];
    const maxImages = 15; // Limit to prevent storage overload

    for (let i = 0; i < Math.min(images.length, maxImages); i++) {
      const img = images[i];
      
      try {
        onProgress?.(`⬆️ Uploading ${i + 1}/${Math.min(images.length, maxImages)}...`);
        
        // Download image (this might fail due to CORS)
        const blob = await this.downloadImage(img.url);
        
        // Upload to Firebase Storage
        const fileName = `auto-harvested/${Date.now()}-${i}-${img.alt.substring(0, 20) || 'image'}`;
        const storageUrl = await assetStorageService.uploadImageBlob(
          blob,
          orgId,
          fileName
        );

        const category = this.categorizeImage(img);

        stored.push({
          url: storageUrl,
          originalUrl: img.url,
          aspectRatio: img.aspectRatio,
          dimensions: img.dimensions,
          alt: img.alt,
          source: 'website',
          uploadedAt: new Date(),
          category
        });
      } catch (error) {
        console.warn(`Failed to harvest image ${img.url}:`, error);
      }
    }

    onProgress?.(`✅ ${stored.length} Bilder erfolgreich hochgeladen!`);
    console.log(`✅ Successfully harvested ${stored.length} images`);

    return stored;
  },

  /**
   * Download image from URL
   */
  async downloadImage(url: string): Promise<Blob> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download image: ${response.status}`);
    }
    return await response.blob();
  },

  /**
   * Categorize image for PMax
   */
  categorizeImage(img: ScrapedImage): StoredAsset['category'] {
    if (img.context.includes('logo')) return 'logo';
    if (img.aspectRatio === '1:1') return 'square';
    if (img.aspectRatio === '1.91:1' || img.aspectRatio === '16:9') return 'landscape';
    if (img.aspectRatio === '4:5') return 'portrait';
    return 'square'; // Default
  }
};

